import { type ReactNode, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  DonutChart,
  ProgressBarChart,
  ComparisonBarChart,
  Spinner,
} from '@react-apps/design-system';
import { useGeneralReport } from '@/modules/sinarem-reports/presentation/hooks/use-general-report';
import { useQuestionDistribution } from '@/modules/sinarem-reports/presentation/hooks/use-question-distribution';
import { useAreaComparison } from '@/modules/sinarem-reports/presentation/hooks/use-area-comparison';
import { useThemeComparison } from '@/modules/sinarem-reports/presentation/hooks/use-theme-comparison';
import type { GeneralReport } from '@/modules/sinarem-reports/domain/entities/general-report.entity';
import { useStudentRanking } from '@/modules/sinarem-reports/presentation/hooks/use-student-ranking';
import { useStudentDetail } from '@/modules/sinarem-reports/presentation/hooks/use-student-detail';
import type { RankingEntry } from '@/modules/sinarem-reports/domain/entities/student-ranking.entity';
import type { StudentDetail } from '@/modules/sinarem-reports/domain/entities/student-detail.entity';
import { useEditions } from '@/modules/editions/presentation/hooks/use-editions';
import type { Edition } from '@/modules/editions/domain/entities/edition.entity';
import { useClassVsGeneral } from '@/modules/global-dashboard/presentation/hooks/use-class-vs-general';
import { useGlobalStudentRanking } from '@/modules/global-dashboard/presentation/hooks/use-global-student-ranking';
import { useStudentVsGeneral } from '@/modules/global-dashboard/presentation/hooks/use-student-vs-general';
import type { GlobalStudentVSGeneral } from '@/modules/global-dashboard/domain/entities/global-student-vs-general.entity';
import type { GlobalStudentRanking as GlobalStudentRankingData } from '@/modules/global-dashboard/domain/entities/global-student-ranking.entity';
import { useClassTri } from '@/modules/tri/presentation/hooks/useClassTri';
import { useClassTriRanking } from '@/modules/tri/presentation/hooks/use-class-tri-ranking';
import { useClassTriExcluded } from '@/modules/tri/presentation/hooks/use-class-tri-excluded';
import { useStudentTri } from '@/modules/tri/presentation/hooks/use-student-tri';
import type {
  StudentTri,
  StudentTriAreaScore,
} from '@/modules/tri/domain/entities/student-try.entity';
import { FEATURE_FLAGS } from '@/lib/feature-flags';
import { useInstitutionStore } from '@/modules/auth/presentation/store/institution.store';
import {
  Section,
  areaColor,
  byArea,
  normalizeArea,
  slugify,
} from './dashboard/shared';
import { QuestionsTab } from './dashboard/questions-tab';
import { EnamedProjectionCard } from './dashboard/enamed-projection-card';
import { usePdfWorkerDownload } from './dashboard/pdf/use-pdf-worker-download';
import type { AreaPerformanceBlock } from './dashboard/pdf/area-performance-pdf-document';
import type { AreaPerformancePdfWorkerRequest } from './dashboard/pdf/area-performance-pdf.worker';

function createAreaPerformancePdfWorker(): Worker {
  return new Worker(
    new URL('./dashboard/pdf/area-performance-pdf.worker.tsx', import.meta.url),
    { type: 'module' },
  );
}
type MainTab = 'global' | 'edicao';
type Tab = 'geral' | 'aluno' | 'questoes';
type RankingSource = 'sinarem' | 'tri';

const TABS: { id: Tab; label: string }[] = [
  { id: 'geral', label: 'Geral' },
  { id: 'aluno', label: 'Aluno' },
];

const EDITION_TABS: { id: Tab; label: string }[] = [
  ...TABS,
  { id: 'questoes', label: 'Questões' },
];

const MAIN_TABS: { id: MainTab; label: string }[] = [
  { id: 'global', label: 'Dashboard Global' },
  { id: 'edicao', label: 'Dashboard por edição' },
];

function formatUtcDate(iso: string, dayOffset = 0): string {
  const d = new Date(iso);
  if (dayOffset !== 0) d.setUTCDate(d.getUTCDate() + dayOffset);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
}

export function DashboardPage() {
  const { data: editions = [], isLoading: editionsLoading } = useEditions();
  const { data: classVsGeneral, isLoading: loadingClassVsGeneral } =
    useClassVsGeneral();

  const { institution } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const base = `/${institution}`;
  const after = location.pathname.startsWith(base)
    ? location.pathname.slice(base.length)
    : location.pathname;
  const segments = after.split('/').filter(Boolean).map(decodeURIComponent);

  const mainTab: MainTab = segments[0] === 'global' ? 'global' : 'edicao';
  const edition = mainTab === 'edicao' ? (segments[1] ?? null) : null;
  const editionTitle = editions.find((e) => e.id === edition)?.title ?? '';
  const activeTab: Tab =
    segments[2] === 'aluno'
      ? 'aluno'
      : segments[2] === 'questoes'
        ? 'questoes'
        : 'geral';
  const activeGlobalTab: Tab = segments[1] === 'aluno' ? 'aluno' : 'geral';
  const editionEmail =
    mainTab === 'edicao' && segments[2] === 'aluno' ? (segments[3] ?? '') : '';
  const globalEmail =
    mainTab === 'global' && segments[1] === 'aluno' ? (segments[2] ?? '') : '';

  const goToMainTab = (tab: MainTab) =>
    navigate(`${base}/${tab === 'global' ? 'global' : 'edition'}`);

  const goToEdition = (id: string) =>
    navigate(`${base}/edition/${encodeURIComponent(id)}`);

  const goToTab = (tab: Tab) => {
    if (!edition) return;
    navigate(`${base}/edition/${encodeURIComponent(edition)}/${tab}`);
  };

  const goToGlobalTab = (tab: Tab) => navigate(`${base}/global/${tab}`);

  const goToEditionStudent = (email: string) => {
    if (!edition) return;
    if (!email) {
      navigate(`${base}/edition/${encodeURIComponent(edition)}/aluno`);
    } else {
      navigate(
        `${base}/edition/${encodeURIComponent(edition)}/aluno/${encodeURIComponent(email)}`,
      );
    }
  };

  const goToGlobalStudent = (email: string) => {
    if (!email) {
      navigate(`${base}/global/aluno`);
    } else {
      navigate(`${base}/global/aluno/${encodeURIComponent(email)}`);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 sm:px-6 lg:px-0">
      <div className="flex gap-3">
        {MAIN_TABS.map((tab) => {
          const isGlobalLocked =
            tab.id === 'global' && !FEATURE_FLAGS.globalDashboard;
          return (
            <button
              key={tab.id}
              type="button"
              disabled={isGlobalLocked}
              onClick={() => !isGlobalLocked && goToMainTab(tab.id)}
              className={[
                'flex-1 items-center gap-2.5 rounded-xl border px-6 py-4 text-sm font-semibold transition-all sm:flex-none sm:text-base',
                isGlobalLocked
                  ? 'border-(--color-border) text-(--color-text-secondary) bg-(--color-bg-primary)/40 cursor-not-allowed opacity-60'
                  : mainTab === tab.id
                    ? 'border-(--color-accent) bg-(--color-accent)/10 text-(--color-accent)'
                    : 'border-(--color-border) text-(--color-text-secondary) hover:border-(--color-accent)/50 hover:text-(--color-text-primary) bg-(--color-bg-primary)/40',
              ].join(' ')}
            >
              {tab.label}
              {isGlobalLocked && (
                <span className="ml-2 inline-block rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium tracking-wide">
                  Em breve
                </span>
              )}
            </button>
          );
        })}
      </div>

      {mainTab === 'global' ? (
        <div className="space-y-6">
          <div className="border-(--color-border) border-b">
            <nav className="flex gap-1">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => goToGlobalTab(tab.id)}
                  className={[
                    'border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                    activeGlobalTab === tab.id
                      ? 'border-(--color-accent) text-(--color-accent)'
                      : 'text-(--color-text-secondary) hover:text-(--color-text-primary) border-transparent',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {activeGlobalTab === 'geral' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                <svg
                  className="h-5 w-5 shrink-0 text-amber-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
                <p className="text-sm font-medium text-amber-200">
                  Na aba <span className="font-bold">Geral</span>, foram
                  considerados apenas os alunos que finalizaram o simulado.
                </p>
              </div>
              <Section title="Evolução por edição">
                <EditionTrendChart
                  series={(classVsGeneral?.series ?? []).map((s) => ({
                    label: s.editionTitle,
                    classAvg: Math.round((s.classAverage ?? 0) * 100),
                    allAvg: Math.round((s.generalAverage ?? 0) * 100),
                  }))}
                  isLoading={loadingClassVsGeneral}
                />
              </Section>
            </div>
          )}
          {activeGlobalTab === 'aluno' && (
            <GlobalAlunoTab
              email={globalEmail}
              onEmailChange={goToGlobalStudent}
            />
          )}
        </div>
      ) : (
        <>
          <div>
            <h1 className="text-(--color-text-primary) text-2xl font-bold">
              Dashboard
            </h1>
          </div>

          <SinaremEditions
            value={edition}
            onChange={goToEdition}
            editions={editions}
            isLoading={editionsLoading}
          />

          {edition === null ? (
            <NoEditionSelected />
          ) : (
            <>
              <div className="border-(--color-border) border-b">
                <nav className="flex gap-1">
                  {EDITION_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => goToTab(tab.id)}
                      className={[
                        'border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                        activeTab === tab.id
                          ? 'border-(--color-accent) text-(--color-accent)'
                          : 'text-(--color-text-secondary) hover:text-(--color-text-primary) border-transparent',
                      ].join(' ')}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {activeTab === 'geral' && edition && (
                <GeralTab editionId={edition} editionTitle={editionTitle} />
              )}
              {activeTab === 'aluno' && edition && (
                <AlunoTab
                  editionId={edition}
                  email={editionEmail}
                  onEmailChange={goToEditionStudent}
                />
              )}
              {activeTab === 'questoes' && edition && (
                <QuestionsTab editionId={edition} editionTitle={editionTitle} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function NoEditionSelected() {
  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 flex flex-col items-center gap-4 rounded-2xl border px-6 py-20 text-center backdrop-blur-sm">
      <div className="bg-(--color-accent)/10 rounded-full p-4">
        <svg
          className="text-(--color-accent) h-8 w-8 opacity-70"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
          />
        </svg>
      </div>
      <div className="max-w-sm">
        <p className="text-(--color-text-primary) text-base font-semibold">
          Nenhuma edição selecionada
        </p>
        <p className="text-(--color-text-secondary) mt-2 text-sm leading-relaxed">
          Selecione uma edição do SINAREM acima para visualizar os dados do
          dashboard.
        </p>
      </div>
    </div>
  );
}

function SinaremEditions({
  value,
  onChange,
  editions,
  isLoading,
}: {
  value: string | null;
  onChange: (v: string) => void;
  editions: Edition[];
  isLoading: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <label
        htmlFor="sinarem-select"
        className="text-(--color-text-secondary) whitespace-nowrap text-sm font-medium"
      >
        Edição
      </label>
      <div className="relative w-full max-w-sm">
        <select
          id="sinarem-select"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={isLoading}
          className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-primary) focus:ring-(--color-accent)/50 w-full cursor-pointer rounded-lg border px-3 py-2 pr-8 text-sm transition-colors focus:outline-none focus:ring-2 disabled:opacity-50"
        >
          <option value="" disabled>
            {isLoading ? 'Carregando edições...' : 'Selecione uma edição...'}
          </option>
          {editions.map((s) => {
            const date = new Date(s.startsAt).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            });
            return (
              <option key={s.id} value={s.id}>
                {s.title} — {date}
              </option>
            );
          })}
        </select>
        <svg
          className="text-(--color-text-secondary) pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>
  );
}

function GeralTab({
  editionId,
  editionTitle,
}: {
  editionId: string;
  editionTitle: string;
}) {
  const { data: generalReport, isLoading: loadingGeneral } =
    useGeneralReport(editionId);
  const { data: distribution, isLoading: loadingDistribution } =
    useQuestionDistribution(editionId);
  const { data: themeComparison } = useThemeComparison(editionId);
  // Mesma queryKey usada dentro de AreaPerformanceCard — reaproveita o cache
  // do react-query, sem disparar requests novos, pra montar o CSV combinado.
  const { data: areaComparison } = useAreaComparison(editionId);
  const { data: classTri } = useClassTri(editionId);

  const distItems =
    distribution?.distribution
      .map((d) => ({
        name: d.area,
        count: d.questions,
        color: areaColor(d.area),
      }))
      .sort((a, b) => byArea(a.name, b.name)) ?? [];

  const perfItems =
    distribution?.performance
      .map((p) => ({
        name: p.area,
        percent: p.percent,
        color: areaColor(p.area),
      }))
      .sort((a, b) => byArea(a.name, b.name)) ?? [];

  const themeAreas =
    themeComparison?.areas
      .map((a) => ({
        areaName: a.area,
        color: areaColor(a.area),
        topics: a.themes.map((t) => ({
          name: t.theme,
          classScore: t.turma * 100,
          allScore: t.allUsers * 100,
        })),
      }))
      .sort((a, b) => byArea(a.areaName, b.areaName)) ?? [];

  const { institutionName } = useInstitutionStore();
  const isFag = institutionName?.toUpperCase() === 'FAG';
  const { isGenerating: isGeneratingAreaPerformancePdf, generate } =
    usePdfWorkerDownload<AreaPerformancePdfWorkerRequest>();

  const handleDownloadAreaPerformancePdf = () => {
    const blocks: AreaPerformanceBlock[] = [];

    const compAreas = (areaComparison?.areas ?? [])
      .map((a) => ({
        name: a.area,
        classScore: a.turma * 100,
        allScore: a.allUsers * 100,
        color: areaColor(a.area),
      }))
      .sort((a, b) => byArea(a.name, b.name));

    if (compAreas.length) {
      blocks.push({
        title: 'Desempenho por área — Desempenho da turma',
        items: compAreas,
      });
    }

    const triAreas = (classTri?.byArea ?? [])
      .map((a) => ({
        name: a.area,
        classScore: a.average,
        allScore: a.general ?? 0,
        color: areaColor(a.area),
      }))
      .sort((a, b) => byArea(a.name, b.name));

    if (triAreas.length) {
      blocks.push({
        title: 'Desempenho por área — Utilizando a TRI',
        items: triAreas,
      });
    }

    themeAreas.forEach((area) => {
      blocks.push({
        title: area.areaName,
        items: area.topics.map((topic) => ({
          name: topic.name,
          classScore: topic.classScore,
          allScore: topic.allScore,
          color: area.color,
        })),
      });
    });

    generate(
      createAreaPerformancePdfWorker,
      {
        blocks,
        editionTitle,
        institutionName: institutionName ?? '',
        isFag,
        generatedAt: new Date().toLocaleDateString('pt-BR'),
      },
      `${slugify(`desempenho-por-area-${editionTitle}`)}.pdf`,
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
        <svg
          className="h-5 w-5 shrink-0 text-amber-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        <p className="text-sm font-medium text-amber-200">
          Na aba <span className="font-bold">Geral</span>, foram considerados
          apenas os alunos que finalizaram o simulado.
        </p>
      </div>
      <EnamedProjectionCard editionId={editionId} editionTitle={editionTitle} />
      <Section title="Relatório Geral">
        <GeralReport data={generalReport} isLoading={loadingGeneral} />
      </Section>
      <Section title="Distribuição de questões">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DonutChart
            title="Distribuição por grande área"
            subtitle={`${distItems.reduce((s, i) => s + i.count, 0)} questões no total`}
            items={distItems}
            isLoading={loadingDistribution}
          />
          <ProgressBarChart
            title="Desempenho da turma"
            subtitle="Média de acertos por grande área"
            items={perfItems}
            isLoading={loadingDistribution}
          />
        </div>
      </Section>
      <div className="-mb-3 flex justify-end">
        <button
          type="button"
          onClick={handleDownloadAreaPerformancePdf}
          disabled={isGeneratingAreaPerformancePdf}
          className="border-(--color-border) text-(--color-text-secondary) hover:border-(--color-accent)/50 hover:text-(--color-text-primary) bg-(--color-bg-primary)/40 flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isGeneratingAreaPerformancePdf ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : (
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 12m0 0 4.5-4.5M12 12V3"
              />
            </svg>
          )}
          {isGeneratingAreaPerformancePdf
            ? 'Gerando PDF...'
            : 'Baixar PDF — Desempenho por área'}
        </button>
      </div>
      <Section title="Desempenho por área">
        <AreaPerformanceCard editionId={editionId} />
      </Section>
      {themeAreas.map((area) => (
        <Section key={area.areaName} title={area.areaName}>
          <ComparisonBarChart
            title={area.areaName}
            items={area.topics.map((t) => ({
              name: t.name,
              primaryScore: t.classScore,
              secondaryScore: t.allScore,
              primaryColor: area.color,
            }))}
            primaryLabel="Turma"
            secondaryLabel="Todos os usuários"
          />
        </Section>
      ))}
    </div>
  );
}

type AreaPerformanceTab = 'desempenho' | 'enamed';

function AreaPerformanceCard({ editionId }: { editionId: string }) {
  const [tab, setTab] = useState<AreaPerformanceTab>('desempenho');
  const { data: areaComparison, isLoading: loadingAreaComparison } =
    useAreaComparison(editionId);
  const { data: classTri, isLoading: loadingClassTri } = useClassTri(editionId);

  const compAreas =
    areaComparison?.areas
      .map((a) => ({
        name: a.area,
        classScore: a.turma * 100,
        allScore: a.allUsers * 100,
        color: areaColor(a.area),
      }))
      .sort((a, b) => byArea(a.name, b.name)) ?? [];

  const triAreas =
    classTri?.byArea
      ?.map((a) => ({
        name: a.area,
        // byArea já vem como nota 0-100 (API converte o theta EAP com
        // NF_DM = theta*DA+MA, igual à nota geral).
        classScore: a.average,
        allScore: a.general ?? 0,
        color: areaColor(a.area),
      }))
      .sort((a, b) => byArea(a.name, b.name)) ?? [];

  const isDesempenho = tab === 'desempenho';

  const headerRight = (
    <div className="inline-flex shrink-0 gap-1.5">
      {(
        [
          { id: 'desempenho', label: 'Desempenho' },
          { id: 'enamed', label: 'Utilizando a TRI' },
        ] as const
      ).map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => setTab(opt.id)}
          className={[
            'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all',
            tab === opt.id
              ? 'border-(--color-accent) bg-(--color-accent)/10 text-(--color-accent)'
              : 'border-(--color-border) text-(--color-text-secondary) hover:border-(--color-accent)/50 hover:text-(--color-text-primary) bg-(--color-bg-primary)/40',
          ].join(' ')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  if (isDesempenho) {
    return (
      <ComparisonBarChart
        title="Desempenho da turma por área em relação a todos os usuários"
        headerRight={headerRight}
        items={compAreas.map((a) => ({
          name: a.name,
          primaryScore: a.classScore,
          secondaryScore: a.allScore,
          primaryColor: a.color,
        }))}
        primaryLabel="Turma"
        secondaryLabel="Todos os usuários"
        isLoading={loadingAreaComparison}
      />
    );
  }

  return (
    <ComparisonBarChart
      title="Utilizando a TRI por área em relação a todos os usuários"
      headerRight={headerRight}
      items={triAreas.map((a) => ({
        name: a.name,
        primaryScore: a.classScore,
        secondaryScore: a.allScore,
        primaryColor: a.color,
      }))}
      primaryLabel="Turma"
      secondaryLabel="Todos os usuários"
      isLoading={loadingClassTri}
    />
  );
}

function GeralReport({
  data,
  isLoading,
}: {
  data?: GeneralReport;
  isLoading?: boolean;
}) {
  const dash = isLoading ? 'Carregando...' : '—';

  const applicationDate = data
    ? `${formatUtcDate(data.applicationDate.startsAt)} a ${formatUtcDate(data.applicationDate.finishesAt, -1)}`
    : dash;

  const participants = data
    ? `${data.participants} / ${data.totalRegistered} alunos`
    : dash;

  const overallAverage = data
    ? `${(data.overallAverage * 100).toFixed(1).replace('.', ',')}%`
    : dash;

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) border-b px-6 py-4">
        <p className="text-(--color-accent) text-sm font-bold">
          Relatório Geral
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2">
        <Cell
          label="Data de aplicação"
          value={applicationDate}
          className="border-(--color-border) border-b sm:border-r"
          icon={
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
              />
            </svg>
          }
        />
        <Cell
          label="PARTICIPARAM DO SIMULADO / TOTAL CADASTRADOS"
          value={participants}
          className="border-(--color-border) border-b"
          icon={
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
              />
            </svg>
          }
        />
        <Cell
          label="Média geral"
          value={overallAverage}
          className="border-(--color-border) border-b sm:border-b-0 sm:border-r"
          icon={
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
              />
            </svg>
          }
        />
        <Cell
          label="Melhor grande área"
          value={data?.bestArea ?? dash}
          className=""
          icon={
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
              />
            </svg>
          }
        />
      </div>
    </div>
  );
}

function Cell({
  label,
  value,
  icon,
  className,
}: {
  label: string;
  value: string;
  icon: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-4 px-6 py-5 ${className}`}
    >
      <div>
        <p className="text-(--color-text-secondary) mb-1 text-xs font-medium uppercase tracking-widest">
          {label}
        </p>
        <p className="text-(--color-text-primary) text-sm font-bold">{value}</p>
      </div>
      <span className="text-(--color-accent) mt-1 shrink-0 opacity-60">
        {icon}
      </span>
    </div>
  );
}

// ── Edition trend chart ───────────────────────────────────────────────────────

type EditionTrendPoint = { label: string; classAvg: number; allAvg: number };

function smoothLinePath(points: [number, number][]): string {
  if (points.length < 2) return '';
  const d: string[] = [`M ${points[0][0]} ${points[0][1]}`];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d.push(`C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2[0]} ${p2[1]}`);
  }
  return d.join(' ');
}

function EditionTrendChart({
  series,
  isLoading,
}: {
  series: EditionTrendPoint[];
  isLoading?: boolean;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-2xl bg-white/5" />;
  }

  if (series.length === 0) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 rounded-2xl border px-6 py-10 text-center backdrop-blur-sm">
        <p className="text-(--color-text-secondary) text-sm">
          Nenhum dado disponível.
        </p>
      </div>
    );
  }

  const EDITION_TREND = series;
  const PAD = { left: 52, right: 24, top: 20, bottom: 36 };
  const W = 800,
    H = 250;
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const yMax = 100;
  const ySteps = [0, 20, 40, 60, 80, 100];
  const n = EDITION_TREND.length;

  const xOf = (i: number) =>
    n === 1 ? PAD.left + chartW / 2 : PAD.left + (i / (n - 1)) * chartW;
  const yOf = (v: number) => PAD.top + chartH - (v / yMax) * chartH;

  const classPoints = EDITION_TREND.map((d, i): [number, number] => [
    xOf(i),
    yOf(d.classAvg),
  ]);
  const allPoints = EDITION_TREND.map((d, i): [number, number] => [
    xOf(i),
    yOf(d.allAvg),
  ]);

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) border-b px-6 py-4">
        <p className="text-(--color-accent) text-sm font-bold">
          Evolução da média por edição
        </p>
        <p className="text-(--color-text-secondary) mt-0.5 text-xs">
          Desempenho médio da turma comparado a todos os usuários em cada edição
          do SINAREM
        </p>
      </div>

      <div className="flex items-center gap-6 px-6 pt-4">
        <div className="flex items-center gap-2">
          <span className="block h-0.5 w-6 rounded-full bg-[#38bdf8]" />
          <span className="text-(--color-text-secondary) text-xs">Turma</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="block h-0.5 w-6 rounded-full bg-[#10b981]" />
          <span className="text-(--color-text-secondary) text-xs">
            Todos os usuários
          </span>
        </div>
      </div>

      <div className="w-full overflow-x-auto px-4 pb-5 pt-2">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 220, minWidth: 500 }}
        >
          {/* Horizontal grid + Y labels */}
          {ySteps.map((step) => (
            <g key={step}>
              <line
                x1={PAD.left}
                y1={yOf(step)}
                x2={W - PAD.right}
                y2={yOf(step)}
                stroke="rgba(255,255,255,0.07)"
                strokeWidth={1}
                strokeDasharray="5 5"
              />
              <text
                x={PAD.left - 8}
                y={yOf(step) + 4}
                textAnchor="end"
                fontSize={10}
                fill="var(--color-text-secondary)"
                opacity={0.65}
              >
                {step}%
              </text>
            </g>
          ))}

          {/* Vertical grid lines */}
          {EDITION_TREND.map((_, i) => (
            <line
              key={i}
              x1={xOf(i)}
              y1={PAD.top}
              x2={xOf(i)}
              y2={H - PAD.bottom}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth={1}
              strokeDasharray="5 5"
            />
          ))}

          {/* All-users line */}
          <path
            d={smoothLinePath(allPoints)}
            fill="none"
            stroke="#10b981"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Class line */}
          <path
            d={smoothLinePath(classPoints)}
            fill="none"
            stroke="#38bdf8"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* All-users dots + value labels */}
          {allPoints.map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r={5} fill="#10b981" />
              <text
                x={x}
                y={y - 10}
                textAnchor="middle"
                fontSize={10}
                fontWeight="bold"
                fill="#10b981"
              >
                {EDITION_TREND[i].allAvg}%
              </text>
            </g>
          ))}

          {/* Class dots + value labels */}
          {classPoints.map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r={5} fill="#38bdf8" />
              <text
                x={x}
                y={y + 18}
                textAnchor="middle"
                fontSize={10}
                fontWeight="bold"
                fill="#38bdf8"
              >
                {EDITION_TREND[i].classAvg}%
              </text>
            </g>
          ))}

          {/* X-axis labels */}
          {EDITION_TREND.map((d, i) => (
            <text
              key={d.label}
              x={xOf(i)}
              y={H - 6}
              textAnchor="middle"
              fontSize={11}
              fill="var(--color-text-secondary)"
              opacity={0.75}
            >
              {d.label}
            </text>
          ))}

          {/* Hit areas per edition column */}
          {EDITION_TREND.map((_, i) => (
            <rect
              key={i}
              x={xOf(i) - 36}
              y={PAD.top}
              width={72}
              height={chartH}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            />
          ))}

          {/* Tooltip */}
          {hoveredIdx !== null &&
            (() => {
              const d = EDITION_TREND[hoveredIdx];
              const tipW = 200;
              const tipH = 82;
              const tipX = Math.min(
                Math.max(xOf(hoveredIdx) - tipW / 2, PAD.left),
                W - PAD.right - tipW,
              );
              const tipY = PAD.top + 2;
              return (
                <g pointerEvents="none">
                  <rect
                    x={tipX}
                    y={tipY}
                    width={tipW}
                    height={tipH}
                    rx={8}
                    fill="rgba(15,23,42,0.93)"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth={0.8}
                  />
                  <text
                    x={tipX + tipW / 2}
                    y={tipY + 22}
                    textAnchor="middle"
                    fontSize={13}
                    fontWeight="bold"
                    fill="white"
                  >
                    {d.label}
                  </text>
                  <circle cx={tipX + 18} cy={tipY + 43} r={4} fill="#38bdf8" />
                  <text
                    x={tipX + 30}
                    y={tipY + 47}
                    fontSize={12}
                    fill="#38bdf8"
                  >
                    Turma: {d.classAvg}%
                  </text>
                  <circle cx={tipX + 18} cy={tipY + 64} r={4} fill="#10b981" />
                  <text
                    x={tipX + 30}
                    y={tipY + 68}
                    fontSize={12}
                    fill="#10b981"
                  >
                    Todos: {d.allAvg}%
                  </text>
                </g>
              );
            })()}
        </svg>
      </div>
    </div>
  );
}

// ── Simulado table ────────────────────────────────────────────────────────────

type DifficultyKey = 'easy' | 'medium' | 'hard' | 'unknown';

const DIFFICULTY_TEMPLATE: {
  label: string;
  key: DifficultyKey;
  color: string;
  bars: [number, number, number];
}[] = [
  { label: 'Fácil', key: 'easy', color: 'text-green-400', bars: [3, 3, 3] },
  { label: 'Média', key: 'medium', color: 'text-yellow-400', bars: [3, 2, 1] },
  { label: 'Difícil', key: 'hard', color: 'text-red-400', bars: [1, 2, 3] },
  {
    label: 'Desconhecida',
    key: 'unknown',
    color: 'text-(--color-text-secondary)',
    bars: [2, 2, 2],
  },
];

function LoadMoreButton({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="flex justify-center">
      <button
        type="button"
        onClick={onClick}
        disabled={isLoading}
        className="border-(--color-accent) bg-(--color-accent)/10 text-(--color-accent) hover:bg-(--color-accent)/20 rounded-xl border px-5 py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isLoading ? 'Carregando...' : 'Ver mais'}
      </button>
    </div>
  );
}

// ── Simulado table ────────────────────────────────────────────────────────────

function MiniBarIcon({ heights, color }: { heights: number[]; color: string }) {
  const max = 3;
  return (
    <span className={`flex items-end gap-px ${color}`}>
      {heights.map((h, i) => (
        <span
          key={i}
          className="inline-block w-1 rounded-sm bg-current"
          style={{ height: `${(h / max) * 12 + 4}px` }}
        />
      ))}
    </span>
  );
}

function TriAreaBreakdown({
  editionId,
  areas,
  headerRight,
}: {
  editionId: string;
  areas: StudentTriAreaScore[];
  headerRight?: ReactNode;
}) {
  const { data: classTri, isLoading: loadingClassTri } = useClassTri(editionId);

  const classByArea = new Map((classTri?.byArea ?? []).map((a) => [a.area, a]));

  const items = [...areas]
    .sort((a, b) => byArea(a.area, b.area))
    .map((a) => {
      const classArea = classByArea.get(a.area);
      return {
        name: a.area,
        // byArea já vem como nota 0-100 (API converte o theta EAP com
        // NF_DM = theta*DA+MA, igual à nota geral).
        primaryScore: a.triScore,
        secondaryScore: classArea?.average ?? 0,
        tertiaryScore: classArea?.general ?? 0,
        primaryColor: areaColor(a.area),
      };
    });

  return (
    <ComparisonBarChart
      title="Projeção por grande área"
      headerRight={headerRight}
      items={items}
      primaryLabel="Aluno"
      secondaryLabel="Turma"
      secondaryColor="#ffffff"
      tertiaryLabel="Todos os usuários"
      tertiaryColor="#64748b"
      isLoading={loadingClassTri}
    />
  );
}

type StudentAreaTab = 'desempenho' | 'enamed';

function StudentAreaPerformanceCard({
  editionId,
  detail,
  studentTri,
}: {
  editionId: string;
  detail: StudentDetail;
  studentTri?: StudentTri;
}) {
  const [tab, setTab] = useState<StudentAreaTab>('desempenho');
  const triAreas = studentTri?.byArea ?? [];
  const hasTriAreas = triAreas.length > 0;

  const headerRight = (
    <div className="inline-flex shrink-0 gap-1.5">
      {(
        [
          { id: 'desempenho', label: 'Desempenho' },
          { id: 'enamed', label: 'Utilizando a TRI' },
        ] as const
      ).map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => setTab(opt.id)}
          disabled={opt.id === 'enamed' && !hasTriAreas}
          className={[
            'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-40',
            tab === opt.id
              ? 'border-(--color-accent) bg-(--color-accent)/10 text-(--color-accent)'
              : 'border-(--color-border) text-(--color-text-secondary) hover:border-(--color-accent)/50 hover:text-(--color-text-primary) bg-(--color-bg-primary)/40',
          ].join(' ')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  if (tab === 'enamed' && hasTriAreas) {
    return (
      <TriAreaBreakdown
        editionId={editionId}
        areas={triAreas}
        headerRight={headerRight}
      />
    );
  }

  return (
    <StudentDesempenhoAreaBreakdown
      editionId={editionId}
      detail={detail}
      headerRight={headerRight}
    />
  );
}

function StudentDesempenhoAreaBreakdown({
  editionId,
  detail,
  headerRight,
}: {
  editionId: string;
  detail: StudentDetail;
  headerRight?: ReactNode;
}) {
  const { data: areaComparison, isLoading: loadingAreaComparison } =
    useAreaComparison(editionId);
  const { data: questionDistribution, isLoading: loadingDistribution } =
    useQuestionDistribution(editionId);

  const classByArea = new Map(
    (areaComparison?.areas ?? []).map((a) => [normalizeArea(a.area), a]),
  );
  const studentByArea = new Map(
    detail.performanceByArea.map((a) => [
      normalizeArea(a.area ?? 'Sem classificação'),
      a,
    ]),
  );

  const examAreas = questionDistribution?.distribution ?? [];
  const areas =
    examAreas.length > 0
      ? examAreas.map((area) => ({
          name: area.area,
          totalQuestions: area.questions,
        }))
      : detail.performanceByArea.map((area) => ({
          name: area.area ?? 'Sem classificação',
          totalQuestions: area.total,
        }));

  const items = areas
    .map((item) => {
      const name = item.name;
      const normalizedName = normalizeArea(name);
      const studentArea = studentByArea.get(normalizedName);
      const classArea = classByArea.get(normalizedName);
      return {
        name,
        primaryScore:
          item.totalQuestions > 0
            ? ((studentArea?.correct ?? 0) / item.totalQuestions) * 100
            : 0,
        secondaryScore: (classArea?.turma ?? 0) * 100,
        tertiaryScore: (classArea?.allUsers ?? 0) * 100,
        primaryColor: areaColor(name),
      };
    })
    .sort((a, b) => byArea(a.name, b.name));

  return (
    <ComparisonBarChart
      title="Desempenho por grande área"
      headerRight={headerRight}
      items={items}
      primaryLabel="Aluno"
      secondaryLabel="Turma"
      secondaryColor="#ffffff"
      tertiaryLabel="Todos os usuários"
      tertiaryColor="#64748b"
      isLoading={loadingAreaComparison || loadingDistribution}
    />
  );
}

function SimuladoSummary({
  totals,
  difficulty,
}: {
  totals: { correct: number; total: number; percent: number };
  difficulty: {
    easy: { correct: number; total: number };
    medium: { correct: number; total: number };
    hard: { correct: number; total: number };
    unknown: { correct: number; total: number };
  };
}) {
  const totalCorrect = totals.correct;
  const totalQuestions = totals.total;
  const pct = Math.round(totals.percent * 100);

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) border-b px-6 py-4">
        <p className="text-(--color-accent) text-sm font-bold">
          Temas do simulado
        </p>
        <p className="text-(--color-text-secondary) mt-0.5 text-xs">
          {totalCorrect} de {totalQuestions} acertos
          <span className="mx-2">·</span>
          {pct}% Desempenho
        </p>
      </div>
      <div className="overflow-x-auto">
        <div className="flex min-w-max items-center">
          {totalQuestions > 0 && (
            <div className="border-(--color-border) flex items-center gap-2.5 border-r px-5 py-3">
              <svg
                className="text-(--color-accent) h-4 w-4 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m4.5 12.75 6 6 9-13.5"
                />
              </svg>
              <div>
                <p className="text-(--color-text-secondary) text-xs">
                  Total de acertos
                </p>
                <p className="text-(--color-text-primary) text-sm font-bold tabular-nums">
                  {totalCorrect}/{totalQuestions} pontos
                </p>
              </div>
            </div>
          )}
          {DIFFICULTY_TEMPLATE.map((stat) => {
            const d = difficulty[stat.key];
            if (d.total === 0) return null;
            return (
              <div
                key={stat.label}
                className="border-(--color-border) flex items-center gap-2.5 border-r px-5 py-3 last:border-r-0"
              >
                <MiniBarIcon heights={stat.bars} color={stat.color} />
                <div>
                  <p className="text-(--color-text-secondary) text-xs">
                    {stat.label}
                  </p>
                  <p className="text-(--color-text-primary) text-sm font-bold tabular-nums">
                    {d.correct}/{d.total} pontos
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function IncidenceBlocks({
  incidence,
  maxIncidence,
}: {
  incidence: number;
  maxIncidence: number;
}) {
  // Mirrors Flutter _IncidenceBlocks: the raw relevance value (e.g. 0.00375) is
  // normalized against the max relevance across all rows, then mapped to
  // 0-10 half-blocks rendered over 5 boxes.
  const percentage = maxIncidence > 0 ? (incidence / maxIncidence) * 100 : 0;
  const halfBlocks = Math.min(10, Math.max(0, Math.ceil(percentage / 10)));
  const color =
    halfBlocks <= 4
      ? 'bg-green-500'
      : halfBlocks <= 8
        ? 'bg-yellow-500'
        : 'bg-red-500';

  return (
    <div
      className="flex items-center justify-end gap-1"
      title={`Relevância: ${(incidence * 100).toFixed(2)}%`}
    >
      {Array.from({ length: 5 }).map((_, i) => {
        const isFull = halfBlocks >= (i + 1) * 2;
        const isHalf = halfBlocks === i * 2 + 1;
        return (
          <span
            key={i}
            className="bg-(--color-border) relative inline-block h-3.5 w-3.5 overflow-hidden rounded-sm"
          >
            {isFull ? (
              <span className={`absolute inset-0 ${color}`} />
            ) : isHalf ? (
              <span className={`absolute inset-y-0 left-0 w-1/2 ${color}`} />
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

type SortKey = 'incidence' | 'performance';

function SortHeader({
  label,
  active,
  ascending,
  onClick,
}: {
  label: string;
  active: boolean;
  ascending: boolean;
  onClick: () => void;
}) {
  return (
    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">
      <button
        type="button"
        onClick={onClick}
        className={`ml-auto flex items-center gap-1.5 transition-colors ${
          active
            ? 'text-(--color-accent)'
            : 'text-(--color-text-secondary) hover:text-(--color-text-primary)'
        }`}
      >
        {label}
        <span
          className={`flex h-5 w-5 items-center justify-center rounded ${
            active
              ? 'bg-(--color-accent)/15 text-(--color-accent)'
              : 'text-(--color-text-secondary) opacity-50'
          }`}
        >
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={
                ascending
                  ? 'M4.5 15.75l7.5-7.5 7.5 7.5'
                  : 'M19.5 8.25l-7.5 7.5-7.5-7.5'
              }
            />
          </svg>
        </span>
      </button>
    </th>
  );
}

function SimuladoTable({
  listing,
}: {
  listing: {
    topic: string;
    tags: string[];
    incidence: number;
    correct: number;
    total: number;
  }[];
}) {
  const [primarySort, setPrimarySort] = useState<SortKey>('incidence');
  const [incidenceAscending, setIncidenceAscending] = useState(false);
  const [performanceAscending, setPerformanceAscending] = useState(true);
  const [filterArea, setFilterArea] = useState('');
  const [visibleCount, setVisibleCount] = useState(10);

  // Distinct grandes áreas (tag[0]) available in the listing, alphabetical.
  const areas = [
    ...new Set(listing.map((t) => t.tags[0]).filter(Boolean)),
  ].sort(byArea);

  // Relevance is normalized against the max across the full listing (not the
  // filtered subset), so the incidence blocks stay stable when filtering.
  const maxIncidence = listing.reduce(
    (m, t) => (t.incidence > m ? t.incidence : m),
    0,
  );

  const filtered = filterArea
    ? listing.filter((t) => t.tags[0] === filterArea)
    : listing;

  const performanceOf = (t: { correct: number; total: number }) =>
    t.total > 0 ? t.correct / t.total : 1;

  type Row = (typeof listing)[number];
  const incidenceCmp = (a: Row, b: Row) => {
    const cmp = a.incidence - b.incidence;
    return incidenceAscending ? cmp : -cmp;
  };
  const performanceCmp = (a: Row, b: Row) => {
    const cmp = performanceOf(a) - performanceOf(b);
    return performanceAscending ? cmp : -cmp;
  };

  const sorted = [...filtered].sort((a, b) => {
    const primary =
      primarySort === 'incidence' ? incidenceCmp(a, b) : performanceCmp(a, b);
    if (primary !== 0) return primary;
    // Tiebreak with the secondary sort (the one not last activated as primary).
    return primarySort === 'incidence'
      ? performanceCmp(a, b)
      : incidenceCmp(a, b);
  });
  const visibleRows = sorted.slice(0, visibleCount);

  const handleSort = (key: SortKey) => {
    if (primarySort === key) {
      if (key === 'incidence') setIncidenceAscending((v) => !v);
      else setPerformanceAscending((v) => !v);
    } else {
      setPrimarySort(key);
    }
  };

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) flex items-center gap-3 border-b px-6 py-4">
        <label
          htmlFor="area-filter"
          className="text-(--color-text-secondary) whitespace-nowrap text-sm font-medium"
        >
          Grande área
        </label>
        <div className="relative w-full max-w-xs">
          <select
            id="area-filter"
            value={filterArea}
            onChange={(e) => {
              setFilterArea(e.target.value);
              setVisibleCount(10);
            }}
            className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-primary) focus:ring-(--color-accent)/50 w-full cursor-pointer rounded-lg border px-3 py-2 pr-8 text-sm transition-colors focus:outline-none focus:ring-2"
          >
            <option value="">Todas as áreas</option>
            {areas.map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
          <svg
            className="text-(--color-text-secondary) pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-(--color-border) border-b">
              <th className="text-(--color-text-secondary) px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                Nome
              </th>
              <SortHeader
                label="Relevância"
                active={primarySort === 'incidence'}
                ascending={incidenceAscending}
                onClick={() => handleSort('incidence')}
              />
              <SortHeader
                label="Desempenho"
                active={primarySort === 'performance'}
                ascending={performanceAscending}
                onClick={() => handleSort('performance')}
              />
            </tr>
          </thead>
          <tbody className="divide-(--color-border) divide-y">
            {visibleRows.map((topic) => {
              return (
                <tr
                  key={topic.topic}
                  className="hover:bg-(--color-bg-secondary) transition-colors"
                >
                  <td className="px-6 py-3">
                    <p className="text-(--color-text-primary) font-medium">
                      {topic.topic}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {(() => {
                        // tag[0] is the area (drives the color); the last tag
                        // duplicates topic.topic, so it is not rendered.
                        const color = areaColor(topic.tags[0] ?? '');
                        return topic.tags.slice(0, -1).map((tag, i) => {
                          const isArea = i === 0;
                          return (
                            <span
                              key={`${tag}-${i}`}
                              className="rounded-full px-2 py-0.5 text-xs font-medium"
                              style={
                                isArea
                                  ? { backgroundColor: `${color}26`, color }
                                  : {
                                      backgroundColor: `${color}14`,
                                      color: `${color}99`,
                                    }
                              }
                            >
                              {tag}
                            </span>
                          );
                        });
                      })()}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <IncidenceBlocks
                      incidence={topic.incidence}
                      maxIncidence={maxIncidence}
                    />
                  </td>
                  <td className="text-(--color-text-secondary) px-6 py-3 text-right font-medium tabular-nums">
                    {topic.correct}/{topic.total}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {visibleCount < sorted.length && (
        <div className="border-(--color-border) border-t px-5 py-3">
          <LoadMoreButton
            onClick={() => setVisibleCount((count) => count + 10)}
            isLoading={false}
          />
        </div>
      )}
    </div>
  );
}

// ── Aluno tab ─────────────────────────────────────────────────────────────────

function buildSegments(
  scores: (number | null)[],
  xOf: (i: number) => number,
  yOf: (v: number) => number,
): [number, number][][] {
  const segments: [number, number][][] = [];
  let current: [number, number][] = [];
  scores.forEach((score, i) => {
    if (score !== null) {
      current.push([xOf(i), yOf(score)]);
    } else {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
    }
  });
  if (current.length > 0) segments.push(current);
  return segments;
}

function StudentTrendChart({
  studentName,
  series,
}: {
  studentName: string;
  series: GlobalStudentVSGeneral['series'];
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (series.length === 0) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 rounded-2xl border px-6 py-10 text-center backdrop-blur-sm">
        <p className="text-(--color-text-secondary) text-sm">
          Nenhum dado disponível.
        </p>
      </div>
    );
  }

  const EDITION_TREND = series.map((s) => ({
    label: s.editionTitle,
    allAvg: Math.round(s.generalAverage * 100),
  }));
  const scores = series.map((s) =>
    s.studentGrade !== null ? Math.round(s.studentGrade * 100) : null,
  );
  const PAD = { left: 52, right: 24, top: 20, bottom: 36 };
  const W = 800,
    H = 250;
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const yMax = 100;
  const ySteps = [0, 25, 50, 75, 100];
  const n = EDITION_TREND.length;
  const midY = PAD.top + chartH / 2;

  const xOf = (i: number) =>
    n === 1 ? PAD.left + chartW / 2 : PAD.left + (i / (n - 1)) * chartW;
  const yOf = (v: number) => PAD.top + chartH - (v / yMax) * chartH;

  const allPoints = EDITION_TREND.map((d, i): [number, number] => [
    xOf(i),
    yOf(d.allAvg),
  ]);
  const segments = buildSegments(scores, xOf, yOf);

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) border-b px-6 py-4">
        <p className="text-(--color-accent) text-sm font-bold">
          Evolução da nota por edição
        </p>
        <p className="text-(--color-text-secondary) mt-0.5 text-xs">
          Nota de {studentName.split(' ')[0]} comparada à média geral em cada
          edição do SINAREM
        </p>
      </div>

      <div className="flex items-center gap-6 px-6 pt-4">
        <div className="flex items-center gap-2">
          <span className="block h-0.5 w-6 rounded-full bg-[#38bdf8]" />
          <span className="text-(--color-text-secondary) text-xs">
            {studentName.split(' ')[0]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="block h-0.5 w-6 rounded-full bg-[#64748b]" />
          <span className="text-(--color-text-secondary) text-xs">
            Média geral
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="block h-3.5 w-3.5 rounded-full border-2 border-dashed border-white/30" />
          <span className="text-(--color-text-secondary) text-xs">
            Não participou
          </span>
        </div>
      </div>

      <div className="w-full overflow-x-auto px-4 pb-5 pt-2">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 220, minWidth: 500 }}
        >
          {/* Horizontal grid + Y labels */}
          {ySteps.map((step) => (
            <g key={step}>
              <line
                x1={PAD.left}
                y1={yOf(step)}
                x2={W - PAD.right}
                y2={yOf(step)}
                stroke="rgba(255,255,255,0.07)"
                strokeWidth={1}
                strokeDasharray="5 5"
              />
              <text
                x={PAD.left - 8}
                y={yOf(step) + 4}
                textAnchor="end"
                fontSize={10}
                fill="var(--color-text-secondary)"
                opacity={0.65}
              >
                {step}%
              </text>
            </g>
          ))}

          {/* Vertical grid lines */}
          {EDITION_TREND.map((_, i) => (
            <line
              key={i}
              x1={xOf(i)}
              y1={PAD.top}
              x2={xOf(i)}
              y2={H - PAD.bottom}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth={1}
              strokeDasharray="5 5"
            />
          ))}

          {/* "Não participou" column shade + markers */}
          {scores.map((score, i) => {
            if (score !== null) return null;
            const x = xOf(i);
            const colW = n === 1 ? chartW : chartW / (n - 1);
            return (
              <g key={i}>
                <rect
                  x={x - colW / 2}
                  y={PAD.top}
                  width={colW}
                  height={chartH}
                  fill="rgba(255,255,255,0.02)"
                  rx={4}
                />
                <circle
                  cx={x}
                  cy={midY}
                  r={22}
                  fill="rgba(255,255,255,0.04)"
                  stroke="rgba(255,255,255,0.18)"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                />
                <text
                  x={x}
                  y={midY - 4}
                  textAnchor="middle"
                  fontSize={9}
                  fill="rgba(255,255,255,0.35)"
                >
                  Não
                </text>
                <text
                  x={x}
                  y={midY + 8}
                  textAnchor="middle"
                  fontSize={9}
                  fill="rgba(255,255,255,0.35)"
                >
                  participou
                </text>
              </g>
            );
          })}

          {/* All-users line (dashed) */}
          <path
            d={smoothLinePath(allPoints)}
            fill="none"
            stroke="#64748b"
            strokeWidth={2}
            strokeDasharray="6 3"
            strokeLinecap="round"
          />

          {/* Student line segments (broken where null) */}
          {segments.map((seg, si) =>
            seg.length >= 2 ? (
              <path
                key={si}
                d={smoothLinePath(seg)}
                fill="none"
                stroke="#38bdf8"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null,
          )}

          {/* All-users dots + labels */}
          {allPoints.map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r={4} fill="#64748b" />
              <text
                x={x}
                y={y + 16}
                textAnchor="middle"
                fontSize={10}
                fontWeight="bold"
                fill="#64748b"
              >
                {EDITION_TREND[i].allAvg}%
              </text>
            </g>
          ))}

          {/* Student dots + labels (only where participated) */}
          {scores.map((score, i) => {
            if (score === null) return null;
            const x = xOf(i);
            const y = yOf(score);
            return (
              <g key={i}>
                <circle cx={x} cy={y} r={5} fill="#38bdf8" />
                <text
                  x={x}
                  y={y - 10}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight="bold"
                  fill="#38bdf8"
                >
                  {score}%
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          {EDITION_TREND.map((d, i) => (
            <text
              key={d.label}
              x={xOf(i)}
              y={H - 6}
              textAnchor="middle"
              fontSize={11}
              fill="var(--color-text-secondary)"
              opacity={0.75}
            >
              {d.label}
            </text>
          ))}

          {/* Hit areas per edition column */}
          {EDITION_TREND.map((_, i) => (
            <rect
              key={i}
              x={xOf(i) - 36}
              y={PAD.top}
              width={72}
              height={chartH}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            />
          ))}

          {/* Tooltip */}
          {hoveredIdx !== null &&
            (() => {
              const d = EDITION_TREND[hoveredIdx];
              const score = scores[hoveredIdx] ?? null;
              const tipW = 160;
              const tipH = 56;
              const tipX = Math.min(
                Math.max(xOf(hoveredIdx) - tipW / 2, PAD.left),
                W - PAD.right - tipW,
              );
              const tipY = PAD.top + 2;
              return (
                <g pointerEvents="none">
                  <rect
                    x={tipX}
                    y={tipY}
                    width={tipW}
                    height={tipH}
                    rx={5}
                    fill="rgba(15,23,42,0.93)"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth={0.8}
                  />
                  <text
                    x={tipX + tipW / 2}
                    y={tipY + 15}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight="bold"
                    fill="white"
                  >
                    {d.label}
                  </text>
                  <circle cx={tipX + 14} cy={tipY + 29} r={3} fill="#38bdf8" />
                  <text x={tipX + 22} y={tipY + 33} fontSize={9} fill="#38bdf8">
                    {studentName.split(' ')[0]}:{' '}
                    {score !== null ? `${score}%` : 'não participou'}
                  </text>
                  <circle cx={tipX + 14} cy={tipY + 44} r={3} fill="#64748b" />
                  <text x={tipX + 22} y={tipY + 48} fontSize={9} fill="#64748b">
                    Média geral: {d.allAvg}%
                  </text>
                </g>
              );
            })()}
        </svg>
      </div>
    </div>
  );
}

function NoSinaremState({ name, email }: { name: string; email: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('');

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="bg-(--color-accent)/20 text-(--color-accent) flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold">
            {initials}
          </div>
          <div>
            <p className="text-(--color-text-primary) text-sm font-semibold">
              {name}
            </p>
            <p className="text-(--color-text-secondary) text-xs">{email}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-5 px-6 py-16 text-center">
        <div className="bg-(--color-accent)/10 rounded-full p-5">
          <svg
            className="text-(--color-accent) h-10 w-10 opacity-70"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
            />
          </svg>
        </div>

        <div className="max-w-sm">
          <p className="text-(--color-text-primary) text-base font-semibold">
            Nenhuma edição realizada
          </p>
          <p className="text-(--color-text-secondary) mt-2 text-sm leading-relaxed">
            {name.split(' ')[0]} ainda não participou de nenhuma edição do
            SINAREM. Os dados aparecerão aqui assim que houver uma participação
            registrada.
          </p>
        </div>
      </div>
    </div>
  );
}

function AlunoTab({
  editionId,
  email,
  onEmailChange,
}: {
  editionId: string;
  email: string;
  onEmailChange: (email: string) => void;
}) {
  const [rankingSource, setRankingSource] = useState<RankingSource>('sinarem');

  const {
    data: rankingPages,
    isLoading: loadingRanking,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useStudentRanking(editionId);
  const ranking = rankingPages?.pages.flatMap((page) => page.ranking) ?? [];

  const selectedEntry = ranking.find((e) => e.email === email) ?? null;
  const hasData = selectedEntry?.hasData ?? false;

  const { data: detail, isLoading: loadingDetail } = useStudentDetail(
    editionId,
    hasData ? email : '',
  );
  const { data: studentTri } = useStudentTri(editionId, hasData ? email : '');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label
          htmlFor="student-select"
          className="text-(--color-text-secondary) whitespace-nowrap text-sm font-medium"
        >
          Aluno
        </label>
        <div className="relative w-full max-w-sm">
          <select
            id="student-select"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={loadingRanking}
            className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-primary) focus:ring-(--color-accent)/50 w-full cursor-pointer rounded-lg border px-3 py-2 pr-8 text-sm transition-colors focus:outline-none focus:ring-2 disabled:opacity-50"
          >
            <option value="" disabled>
              {loadingRanking ? 'Carregando...' : 'Selecione um aluno...'}
            </option>
            {ranking.map((entry) => (
              <option key={entry.email} value={entry.email}>
                {entry.name}
              </option>
            ))}
          </select>
          <svg
            className="text-(--color-text-secondary) pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {email && (
        <button
          type="button"
          onClick={() => onEmailChange('')}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Voltar ao ranking
        </button>
      )}

      {email ? (
        !hasData ? (
          <NoSinaremState name={selectedEntry?.name ?? email} email={email} />
        ) : loadingDetail ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-2xl bg-white/5"
              />
            ))}
          </div>
        ) : detail ? (
          <>
            <Section title="Perfil">
              <StudentCard detail={detail} enamedScore={studentTri?.average} />
            </Section>
            <Section title="Temas do simulado">
              <SimuladoSummary
                totals={detail.totals}
                difficulty={detail.difficulty}
              />
            </Section>
            <Section title="Desempenho por grande área">
              <StudentAreaPerformanceCard
                editionId={editionId}
                detail={detail}
                studentTri={studentTri}
              />
            </Section>
            <Section title="Listagem">
              <SimuladoTable listing={detail.listing} />
            </Section>
          </>
        ) : null
      ) : (
        <Section title="Ranking da instituição">
          <div className="flex justify-end">
            <RankingSourceToggle
              value={rankingSource}
              onChange={setRankingSource}
            />
          </div>
          {rankingSource === 'sinarem' ? (
            <StudentRanking
              ranking={ranking}
              isLoading={loadingRanking}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              onLoadMore={() => fetchNextPage()}
              onSelect={onEmailChange}
            />
          ) : (
            <>
              <TriClassRanking editionId={editionId} onSelect={onEmailChange} />
              <ExcludedFromRanking
                editionId={editionId}
                onSelect={onEmailChange}
              />
            </>
          )}
        </Section>
      )}
    </div>
  );
}

function StudentRanking({
  ranking,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
}: {
  ranking: RankingEntry[];
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (email: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
        <div className="space-y-2 px-5 py-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-white/10"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="divide-(--color-border) divide-y">
        {ranking.map((entry) => {
          const initials = entry.name
            .split(' ')
            .slice(0, 2)
            .map((n) => n[0])
            .join('');
          const pos = entry.position;

          return (
            <button
              key={entry.email}
              type="button"
              onClick={() => onSelect(entry.email)}
              className="hover:bg-(--color-accent)/5 flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors"
            >
              <span className="flex w-12 shrink-0 items-center gap-1">
                {pos !== null && pos <= 3 ? (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      className={`h-4 w-4 shrink-0 ${pos === 1 ? 'text-yellow-400' : pos === 2 ? 'text-slate-400' : 'text-amber-600'}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
                      />
                    </svg>
                    <span
                      className={`text-sm font-bold tabular-nums ${pos === 1 ? 'text-yellow-400' : pos === 2 ? 'text-slate-400' : 'text-amber-600'}`}
                    >
                      {pos}
                    </span>
                  </>
                ) : pos !== null ? (
                  <span className="text-(--color-text-secondary) text-sm font-bold tabular-nums opacity-60">
                    {pos}
                  </span>
                ) : (
                  <span className="text-(--color-text-secondary) text-sm opacity-40">
                    —
                  </span>
                )}
              </span>

              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  entry.hasData
                    ? 'bg-(--color-accent)/20 text-(--color-accent)'
                    : 'bg-(--color-border) text-(--color-text-secondary)'
                }`}
              >
                {initials}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-(--color-text-primary) truncate text-sm font-medium">
                  {entry.name}
                </p>
                <p className="text-(--color-text-secondary) truncate text-xs">
                  {entry.email}
                </p>
              </div>

              {entry.score !== null ? (
                <span className="border-(--color-border) text-(--color-text-primary) shrink-0 rounded-lg border px-3 py-1 text-sm font-bold tabular-nums">
                  {entry.score}
                </span>
              ) : (
                <span className="text-(--color-text-secondary) shrink-0 text-xs italic">
                  sem dados
                </span>
              )}
            </button>
          );
        })}
      </div>
      {hasMore && (
        <div className="border-(--color-border) border-t px-5 py-3">
          <LoadMoreButton onClick={onLoadMore} isLoading={isLoadingMore} />
        </div>
      )}
    </div>
  );
}

function RankingSourceToggle({
  value,
  onChange,
}: {
  value: RankingSource;
  onChange: (source: RankingSource) => void;
}) {
  const options: { id: RankingSource; label: string }[] = [
    { id: 'sinarem', label: 'SINAREM' },
    { id: 'tri', label: 'Utilizando a TRI' },
  ];

  return (
    <div className="inline-flex gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={[
            'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all',
            value === opt.id
              ? 'border-(--color-accent) bg-(--color-accent)/10 text-(--color-accent)'
              : 'border-(--color-border) text-(--color-text-secondary) hover:border-(--color-accent)/50 hover:text-(--color-text-primary) bg-(--color-bg-primary)/40',
          ].join(' ')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function TriClassRanking({
  editionId,
  onSelect,
}: {
  editionId: string;
  onSelect: (email: string) => void;
}) {
  const {
    data: rankingPages,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClassTriRanking(editionId);
  const ranking = rankingPages?.pages.flatMap((p) => p.ranking) ?? [];

  if (isLoading) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 mt-3 overflow-hidden rounded-2xl border backdrop-blur-sm">
        <div className="space-y-2 px-5 py-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-white/10"
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 mt-3 rounded-2xl border px-6 py-10 text-center backdrop-blur-sm">
        <p className="text-(--color-text-secondary) text-sm">
          Cálculo utilizando a TRI ainda não realizado.
        </p>
      </div>
    );
  }

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 mt-3 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="divide-(--color-border) divide-y">
        {ranking.map((entry) => {
          const name = entry.name;
          const initials = name
            .split(' ')
            .slice(0, 2)
            .map((n) => n[0])
            .join('');
          const pos = entry.position;

          return (
            <button
              key={entry.email}
              type="button"
              onClick={() => onSelect(entry.email)}
              className="hover:bg-(--color-accent)/5 flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors"
            >
              <span className="flex w-12 shrink-0 items-center gap-1">
                {pos <= 3 ? (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      className={`h-4 w-4 shrink-0 ${pos === 1 ? 'text-yellow-400' : pos === 2 ? 'text-slate-400' : 'text-amber-600'}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
                      />
                    </svg>
                    <span
                      className={`text-sm font-bold tabular-nums ${pos === 1 ? 'text-yellow-400' : pos === 2 ? 'text-slate-400' : 'text-amber-600'}`}
                    >
                      {pos}
                    </span>
                  </>
                ) : (
                  <span className="text-(--color-text-secondary) text-sm font-bold tabular-nums opacity-60">
                    {pos}
                  </span>
                )}
              </span>

              <div className="bg-(--color-accent)/20 text-(--color-accent) flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
                {initials}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-(--color-text-primary) truncate text-sm font-medium">
                  {name}
                </p>
                <p className="text-(--color-text-secondary) truncate text-xs">
                  {entry.email}
                </p>
              </div>

              <span className="border-(--color-border) text-(--color-text-primary) w-14 shrink-0 rounded-lg border px-3 py-1 text-center text-sm font-bold tabular-nums">
                {entry.average.toFixed(1)}
              </span>
            </button>
          );
        })}
      </div>
      {hasNextPage && (
        <div className="border-(--color-border) border-t px-5 py-3">
          <LoadMoreButton
            onClick={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
          />
        </div>
      )}
    </div>
  );
}

function ExcludedFromRanking({
  editionId,
  onSelect,
}: {
  editionId: string;
  onSelect: (email: string) => void;
}) {
  const {
    data: excludedPages,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClassTriExcluded(editionId);
  const excluded = excludedPages?.pages.flatMap((p) => p.excluded) ?? [];

  if (isLoading) {
    return (
      <div className="mt-3 space-y-2 pt-4">
        <div className="h-24 animate-pulse rounded-2xl bg-white/5" />
      </div>
    );
  }

  if (!excluded.length) {
    return null;
  }

  return (
    <div className="mt-3 space-y-2 pt-4">
      <h3 className="text-(--color-text-secondary) text-xs font-semibold uppercase tracking-widest">
        Excluídos do Ranking
      </h3>
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
        <svg
          className="h-5 w-5 shrink-0 text-amber-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        <p className="text-sm font-medium text-amber-200">
          Desconsiderados por não terem realizado pelo menos 80% da prova
        </p>
      </div>
      <div className="border-(--color-border) bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
        <div className="divide-(--color-border) divide-y">
          {excluded.map((entry) => {
            const initials = entry.name
              .split(' ')
              .slice(0, 2)
              .map((n) => n[0])
              .join('');

            return (
              <button
                key={entry.email}
                type="button"
                onClick={() => onSelect(entry.email)}
                className="hover:bg-(--color-accent)/5 flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors"
              >
                <span className="text-(--color-text-secondary) flex w-12 shrink-0 items-center justify-center text-sm opacity-60">
                  —
                </span>

                <div className="bg-(--color-accent)/20 text-(--color-accent) flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
                  {initials}
                </div>

                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="min-w-0">
                    <p className="text-(--color-text-primary) truncate text-sm font-medium">
                      {entry.name}
                    </p>
                    <p className="text-(--color-text-secondary) truncate text-xs">
                      {entry.email}
                    </p>
                  </div>

                  <span className="shrink-0 whitespace-nowrap rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold text-amber-400">
                    {entry.reason}
                  </span>
                </div>

                {entry.score !== null ? (
                  <span className="border-(--color-border) text-(--color-text-primary) w-14 shrink-0 rounded-lg border px-3 py-1 text-center text-sm font-bold tabular-nums">
                    {entry.score.toFixed(1)}
                  </span>
                ) : (
                  <span className="text-(--color-text-secondary) w-14 shrink-0 text-center text-xs italic">
                    sem dados
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {hasNextPage && (
          <div className="border-(--color-border) border-t px-5 py-3">
            <LoadMoreButton
              onClick={() => fetchNextPage()}
              isLoading={isFetchingNextPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function GlobalStudentRanking({
  ranking,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
}: {
  ranking: GlobalStudentRankingData['ranking'];
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (email: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
        <div className="space-y-2 px-5 py-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-white/10"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="divide-(--color-border) divide-y">
        {ranking.map((entry) => {
          const hasScore = entry.sinaremsDone > 0;
          const initials = entry.name
            .split(' ')
            .slice(0, 2)
            .map((n) => n[0])
            .join('');
          const position = entry.position;

          return (
            <button
              key={entry.email}
              type="button"
              onClick={() => onSelect(entry.email)}
              className="hover:bg-(--color-accent)/5 flex w-full cursor-pointer items-center gap-4 px-5 py-4 text-left transition-colors"
            >
              <span className="flex w-12 shrink-0 items-center gap-1">
                {position <= 3 ? (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      className={`h-4 w-4 shrink-0 ${position === 1 ? 'text-yellow-400' : position === 2 ? 'text-slate-400' : 'text-amber-600'}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
                      />
                    </svg>
                    <span
                      className={`text-sm font-bold tabular-nums ${position === 1 ? 'text-yellow-400' : position === 2 ? 'text-slate-400' : 'text-amber-600'}`}
                    >
                      {position}
                    </span>
                  </>
                ) : (
                  <span className="text-(--color-text-secondary) text-sm font-bold tabular-nums opacity-60">
                    {position}
                  </span>
                )}
              </span>

              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  hasScore
                    ? 'bg-(--color-accent)/20 text-(--color-accent)'
                    : 'bg-(--color-border) text-(--color-text-secondary)'
                }`}
              >
                {initials}
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-(--color-text-primary) truncate text-sm font-medium">
                  {entry.name}
                </p>
                <p className="text-(--color-text-secondary) truncate text-xs">
                  {entry.email}
                </p>
              </div>

              <span className="border-(--color-border) text-(--color-text-primary) shrink-0 rounded-lg border px-3 py-1 text-sm font-bold tabular-nums">
                {entry.sinaremsDone}{' '}
                {entry.sinaremsDone === 1 ? 'simulado' : 'simulados'}
              </span>
            </button>
          );
        })}
      </div>
      {hasMore && (
        <div className="border-(--color-border) border-t px-5 py-3">
          <LoadMoreButton onClick={onLoadMore} isLoading={isLoadingMore} />
        </div>
      )}
    </div>
  );
}

function GlobalStudentCard({
  name,
  email,
  sinaremsDone,
  totalEditions,
}: {
  name: string;
  email: string;
  sinaremsDone: number;
  totalEditions: number;
}) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('');

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="bg-(--color-accent)/5 border-(--color-border) flex items-center gap-4 border-b px-6 py-5">
        <div className="bg-(--color-accent)/20 text-(--color-accent) flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-bold">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-(--color-text-primary) font-semibold">{name}</p>
          <p className="text-(--color-text-secondary) truncate text-sm">
            {email}
          </p>
        </div>
        <div className="border-(--color-border) flex flex-col items-center rounded-xl border px-5 py-2.5">
          <span className="text-(--color-text-primary) text-3xl font-bold tabular-nums leading-none">
            {sinaremsDone}
            <span className="text-(--color-text-secondary) text-lg font-medium">
              /{totalEditions}
            </span>
          </span>
          <span className="text-(--color-text-secondary) mt-1 text-xs font-medium uppercase tracking-widest">
            SIMULADOS
          </span>
        </div>
      </div>
    </div>
  );
}

function GlobalAlunoTab({
  email,
  onEmailChange,
}: {
  email: string;
  onEmailChange: (email: string) => void;
}) {
  const {
    data: rankingPages,
    isLoading: loadingRanking,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGlobalStudentRanking();
  const ranking = rankingPages?.pages.flatMap((p) => p.ranking) ?? [];

  const selectedEntry = ranking.find((e) => e.email === email) ?? null;

  const { data: studentVsGeneral, isLoading: loadingDetail } =
    useStudentVsGeneral(email);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label
          htmlFor="global-student-select"
          className="text-(--color-text-secondary) whitespace-nowrap text-sm font-medium"
        >
          Aluno
        </label>
        <div className="relative w-full max-w-sm">
          <select
            id="global-student-select"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={loadingRanking}
            className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-primary) focus:ring-(--color-accent)/50 w-full cursor-pointer rounded-lg border px-3 py-2 pr-8 text-sm transition-colors focus:outline-none focus:ring-2 disabled:opacity-50"
          >
            <option value="" disabled>
              {loadingRanking ? 'Carregando...' : 'Selecione um aluno...'}
            </option>
            {ranking.map((entry) => (
              <option key={entry.email} value={entry.email}>
                {entry.name}
              </option>
            ))}
          </select>
          <svg
            className="text-(--color-text-secondary) pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {email && (
        <button
          type="button"
          onClick={() => onEmailChange('')}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Voltar ao ranking
        </button>
      )}

      {email ? (
        selectedEntry === null ? null : loadingDetail ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-24 animate-pulse rounded-2xl bg-white/5"
              />
            ))}
          </div>
        ) : studentVsGeneral ? (
          <>
            <GlobalStudentCard
              name={selectedEntry.name}
              email={selectedEntry.email}
              sinaremsDone={selectedEntry.sinaremsDone}
              totalEditions={studentVsGeneral.series.length}
            />
            <Section title="Evolução da nota por edição">
              <StudentTrendChart
                studentName={selectedEntry.name}
                series={studentVsGeneral.series}
              />
            </Section>
          </>
        ) : null
      ) : (
        <Section title="Ranking Global">
          <GlobalStudentRanking
            ranking={ranking}
            isLoading={loadingRanking}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onSelect={onEmailChange}
          />
        </Section>
      )}
    </div>
  );
}

function StudentCard({
  detail,
  enamedScore,
}: {
  detail: StudentDetail;
  enamedScore?: number;
}) {
  const raw = detail.applicationDate;
  const formattedDate =
    raw.length === 8
      ? `${raw.slice(6, 8)}/${raw.slice(4, 6)}/${raw.slice(0, 4)}`
      : raw;

  const initials = detail.profile.name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('');

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      {/* Header */}
      <div className="bg-(--color-accent)/5 border-(--color-border) flex items-center gap-4 border-b px-6 py-5">
        <div className="bg-(--color-accent)/20 text-(--color-accent) flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-bold">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-(--color-text-primary) font-semibold">
            {detail.profile.name}
          </p>
          <p className="text-(--color-text-secondary) truncate text-sm">
            {detail.profile.email}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="border-(--color-border) flex flex-col items-center rounded-xl border px-5 py-2.5">
            <span className="text-(--color-text-primary) text-3xl font-bold tabular-nums leading-none">
              {detail.score}
            </span>
            <span className="text-(--color-text-secondary) mt-1 text-xs font-medium uppercase tracking-widest">
              SINAREM
            </span>
          </div>
          {enamedScore !== undefined && (
            <div className="border-(--color-accent)/40 flex flex-col items-center rounded-xl border px-5 py-2.5">
              <span className="text-(--color-accent) text-3xl font-bold tabular-nums leading-none">
                {enamedScore.toFixed(1).replace('.', ',')}
              </span>
              <span className="text-(--color-text-secondary) mt-1 text-xs font-medium uppercase tracking-widest">
                TRI
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="border-(--color-border) border-b px-6 py-4 sm:border-r">
          <p className="text-(--color-text-secondary) mb-1 text-xs font-medium uppercase tracking-widest">
            Data do SINAREM
          </p>
          <p className="text-(--color-text-primary) text-sm font-medium">
            {formattedDate}
          </p>
        </div>
        <div className="border-(--color-border) border-b px-6 py-4">
          <p className="text-(--color-text-secondary) mb-1 text-xs font-medium uppercase tracking-widest">
            Posição no Ranking da Instituição
          </p>
          <p className="text-(--color-text-primary) text-sm font-bold">
            {detail.institutionRankingPosition}º lugar
          </p>
        </div>
        {detail.profile.institutionTargets.length > 0 && (
          <div className="col-span-1 px-6 py-4 sm:col-span-2">
            <p className="text-(--color-text-secondary) mb-3 text-xs font-medium uppercase tracking-widest">
              Instituições Alvo
            </p>
            <div className="flex flex-wrap gap-2">
              {detail.profile.institutionTargets.map((target, i) => (
                <span
                  key={`${target.uf}-${target.institution}`}
                  className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) rounded-lg border px-3 py-1 text-sm font-medium"
                >
                  <span className="text-(--color-accent) mr-1.5 font-bold">
                    {i + 1}.
                  </span>
                  {target.uf} - {target.institution}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
